import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as teamSpaceApi from '../services/teamSpaceService';

// CHG-132: 보관 필터 옵션
export function useTeamSpaces(archived: teamSpaceApi.TeamSpaceArchivedFilter = 'active') {
  return useQuery({
    queryKey: ['team-spaces', archived],
    queryFn: () => teamSpaceApi.fetchTeamSpaces(archived),
  });
}

export function useTeamSpace(spaceId?: string) {
  return useQuery({
    queryKey: ['team-spaces', spaceId],
    queryFn: () => teamSpaceApi.fetchTeamSpace(spaceId!),
    enabled: !!spaceId,
  });
}

export function useCreateTeamSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: teamSpaceApi.createTeamSpace,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-spaces'] }),
  });
}

export function useUpdateTeamSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, data }: { spaceId: string; data: Parameters<typeof teamSpaceApi.updateTeamSpace>[1] }) =>
      teamSpaceApi.updateTeamSpace(spaceId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-spaces'] }),
  });
}

export function useDeleteTeamSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: teamSpaceApi.deleteTeamSpace,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team-spaces'] }),
  });
}

export function useAddTeamSpaceMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, data }: { spaceId: string; data: { userId: string; role?: 'MANAGER' | 'MEMBER' } }) =>
      teamSpaceApi.addTeamSpaceMember(spaceId, data),
    onSuccess: (_data, { spaceId }) => qc.invalidateQueries({ queryKey: ['team-spaces', spaceId] }),
  });
}

export function useUpdateTeamSpaceMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, memberId, data }: { spaceId: string; memberId: string; data: { role: 'OWNER' | 'MANAGER' | 'MEMBER' } }) =>
      teamSpaceApi.updateTeamSpaceMemberRole(spaceId, memberId, data),
    onSuccess: (_data, { spaceId }) => qc.invalidateQueries({ queryKey: ['team-spaces', spaceId] }),
  });
}

export function useRemoveTeamSpaceMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ spaceId, memberId }: { spaceId: string; memberId: string }) =>
      teamSpaceApi.removeTeamSpaceMember(spaceId, memberId),
    onSuccess: (_data, { spaceId }) => qc.invalidateQueries({ queryKey: ['team-spaces', spaceId] }),
  });
}
